import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { editTask, rescheduleTask } from "~/store/actions/actions.tasks.jsx";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users.jsx";
import { TaskOptions } from "./TaskOptions.jsx";

export const TaskOptionsContainer = ({ id, projectId, ...rest }) => {
  const dispatch = useDispatch();

  const task = useSelector((state) => getTaskById(state, id));
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));
  const autoReschedule = useSelector((state) => getRawConfigOption(state, "auto_reschedule_enabled"));
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const showTaskEstimatesToClients = useSelector((state) =>
    getRawConfigOption(state, "show_task_estimates_to_clients")
  );

  const handleChangeOptions = useCallback(
    (attributes) => {
      const { dates, ...restAttributes } = attributes;

      let dateAttributes = {};

      if (dates) {
        dateAttributes = { start_on: null, due_on: null };
        if (dates.from && dates.to) {
          dateAttributes.start_on = dates.from;
          dateAttributes.due_on = dates.to;

          if (autoReschedule) {
            return dispatch(rescheduleTask(id, dateAttributes));
          }
        }
      }

      return dispatch(editTask(projectId, id, { ...restAttributes, ...dateAttributes }));
    },
    [dispatch, projectId, id, autoReschedule]
  );

  const values = useMemo(
    () => ({
      taskName: task?.name,
      taskListId: task?.task_list_id,
      assigneeId: task?.assignee_id,
      estimate: task?.estimate,
      jobTypeId: task?.job_type_id,
      hiddenFromClients: task?.is_hidden_from_clients,
      important: task?.is_important,
      startOn: task?.start_on,
      dueOn: task?.due_on,
      isSplit: task?.is_split,
    }),
    [task]
  );

  const selectedLabelIds = useMemo(() => {
    return task?.labels ? task.labels.toArray() : [];
  }, [task]);

  return (
    <TaskOptions
      {...rest}
      {...values}
      labels={selectedLabelIds}
      id={id}
      projectId={projectId}
      onChange={handleChangeOptions}
      hideFromClients={hideFromClients}
      isLoggedUserClient={isLoggedUserClient}
      showTaskEstimatesToClients={showTaskEstimatesToClients}
    />
  );
};

TaskOptionsContainer.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  projectId: PropTypes.number.isRequired,
};
